import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const SellerTermsAndConditions = () => {
  return (
    <div className="p-4 space-y-3 text-sm text-[#404040] leading-6">
      <p>
        <strong>Effective Date:</strong> 01 Aug 2026
        <br />
        <strong>Last Updated:</strong> 01 Aug 2026
      </p>
      <p>
        These Seller Terms and Conditions (&quot;Seller Terms&quot;) govern your
        onboarding, listing, and sale of goods/services on{" "}
        <strong>Mumin&#39;s Mart</strong> operated by{" "}
        <strong>Fluxart Solutions Private Limited</strong>.
      </p>
      <p>
        By registering or operating as a seller, you agree to these Seller
        Terms, the Privacy Policy, and related Platform policies.
      </p>
      <h2 className="font-semibold">1. Seller Eligibility and Authority</h2>
      <ul>
        <li>
          You represent that you are legally capable of contracting under Indian
          law.
        </li>
        <li>
          If you are acting on behalf of a business entity, you confirm
          authority to bind that entity.
        </li>
        <li>
          You must provide true, accurate, and complete onboarding information.
        </li>
      </ul>
      <h2 className="font-semibold">
        2. Onboarding, KYC, and Compliance Documentation
      </h2>
      <p>
        You must provide and maintain valid documentation, including where
        applicable:
      </p>
      <ul>
        <li>PAN, GSTIN, CIN/LLPIN, shop and establishment/trade licenses.</li>
        <li>Bank account and settlement details.</li>
        <li>Authorized signatory and beneficial ownership details.</li>
        <li>Any category-specific permits, certifications, or approvals.</li>
      </ul>
      <p>
        We may suspend onboarding, listing, payouts, or account access if
        documents are invalid, expired, or non-compliant.
      </p>
      <h2 className="font-semibold">3. Seller Obligations</h2>
      <p>You agree to:</p>
      <ul>
        <li>
          List only lawful products/services and maintain accurate descriptions,
          pricing, images, and specifications.
        </li>
        <li>
          Correctly enter applicable HSN codes and GST rates for listed
          products/services. Any loss, liability, penalty, or issue arising from
          incorrect HSN/GST details entered by the seller shall be the
          seller&#39;s sole responsibility and not the responsibility of
          Mumin&#39;s Mart.
        </li>
        <li>
          Ensure product quality, safety, packaging, labeling, and legal
          metrology compliance.
        </li>
        <li>
          Honor warranties/guarantees and after-sales commitments represented to
          customers.
        </li>
        <li>
          Fulfill orders within committed timelines and maintain service levels.
        </li>
        <li>
          Comply with all applicable Indian laws, including consumer, tax, IP,
          and e-commerce regulations.
        </li>
      </ul>
      <h2 className="font-semibold">4. Prohibited Listings and Conduct</h2>
      <p>You must not list, sell, or promote:</p>
      <ul>
        <li>
          Counterfeit, infringing, unsafe, expired, banned, or restricted
          goods/services.
        </li>
        <li>Any substance containing alcohol.</li>
        <li>
          Products requiring licenses/approvals without valid authorization.
        </li>
        <li>Misleading, deceptive, or illegal offerings.</li>
      </ul>
      <p>
        You must not manipulate ratings/reviews, engage in fake orders, or
        perform fraudulent activities.
      </p>
      <h2 className="font-semibold">5. Pricing, Fees, and Taxes</h2>
      <ul>
        <li>
          You are responsible for setting lawful prices and ensuring tax
          accuracy.
        </li>
        <li>
          Platform fees, commissions, logistics charges, penalties, and other
          deductions may apply per commercial terms.
        </li>
        <li>
          You are responsible for invoicing and tax compliance, including GST
          and any applicable withholding/collection obligations.
        </li>
        <li>
          We may deduct/withhold amounts for returns, chargebacks, penalties,
          liabilities, or legal obligations.
        </li>
      </ul>
      <h2 className="font-semibold">
        6. Order Fulfillment, Returns, and Customer Support
      </h2>
      <ul>
        <li>
          You are responsible for order confirmation, packaging, dispatch, and
          delivery performance.
        </li>
        <li>
          You must adhere to Platform return, replacement, and refund rules.
        </li>
        <li>
          You will promptly resolve customer complaints and cooperate in dispute
          resolution.
        </li>
        <li>
          If you fail to dispatch a confirmed order or supply defective
          products, Mumin&#39;s Mart may refund the customer and deduct the
          refunded amount (including related charges, where applicable) from
          your seller wallet and/or settlements.
        </li>
      </ul>
      <h2 className="font-semibold">7. Settlements and Reconciliations</h2>
      <ul>
        <li>
          Payouts are subject to successful order completion, return windows,
          fraud checks, and policy compliance.
        </li>
        <li>
          Settlement statements and reconciliations provided by the Platform are
          deemed accepted unless disputed within 7 days.
        </li>
        <li>
          We may hold settlements during investigations, high-risk events, legal
          notices, or compliance checks.
        </li>
      </ul>
      <h2 className="font-semibold">8. Intellectual Property</h2>
      <ul>
        <li>
          You grant Fluxart Solutions Private Limited a non-exclusive,
          royalty-free license to use your trademarks, logos, and listing
          content for Platform operations and marketing.
        </li>
        <li>
          You warrant that your content and products do not infringe third-party
          intellectual property rights.
        </li>
        <li>
          On notice of alleged infringement, we may remove listings and take
          appropriate action.
        </li>
      </ul>
      <h2 className="font-semibold">9. Data Protection and Confidentiality</h2>
      <ul>
        <li>
          You must process customer data only for order fulfillment and
          permitted support purposes.
        </li>
        <li>
          You must implement reasonable security measures and not misuse or
          share customer data unlawfully.
        </li>
        <li>
          You must comply with applicable privacy/data protection law, including
          DPDP Act obligations where relevant.
        </li>
      </ul>
      <h2 className="font-semibold">
        10. Audits, Monitoring, and Investigations
      </h2>
      <p>
        We may monitor listings and transactions and request records/documents
        to verify compliance. Failure to cooperate may result in suspension or
        termination.
      </p>
      <h2 className="font-semibold">
        11. Suspension, Delisting, and Termination
      </h2>
      <p>
        We may suspend/delist/terminate seller accounts for policy violations,
        legal non-compliance, fraud risk, repeated customer complaints, or
        reputational/security concerns.
      </p>
      <p>
        Upon termination, obligations related to pending orders, refunds,
        disputes, data, indemnity, and legal compliance survive.
      </p>
      <h2 className="font-semibold">12. Representations and Warranties</h2>
      <p>You represent and warrant that:</p>
      <ul>
        <li>All onboarding and listing information is accurate and current.</li>
        <li>
          Products/services and business operations comply with applicable law.
        </li>
        <li>
          You hold all rights, approvals, and licenses required for listed
          items.
        </li>
      </ul>
      <h2 className="font-semibold">13. Indemnity</h2>
      <p>
        You agree to indemnify and hold harmless Fluxart Solutions Private
        Limited, affiliates, and personnel from claims, losses, liabilities,
        penalties, and costs arising from:
      </p>
      <ul>
        <li>Product defects, safety issues, non-compliance, or recalls.</li>
        <li>IP infringement claims.</li>
        <li>Misrepresentation, fraud, or violation of law/policy.</li>
        <li>Breach of these Seller Terms.</li>
      </ul>
      <h2 className="font-semibold">14. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, Fluxart Solutions Private
        Limited shall not be liable for indirect or consequential damages.
        Aggregate liability for direct claims shall be limited to fees
        paid/payable by you to the Platform during the preceding 3 months
        related to the claim.
      </p>
      <p>Nothing limits liability that cannot be excluded by law.</p>
      <h2 className="font-semibold">15. Force Majeure</h2>
      <p>
        Neither party is liable for delay/failure caused by events beyond
        reasonable control, including natural disasters, war, strikes, cyber
        incidents, or government restrictions.
      </p>
      <h2 className="font-semibold">16. Changes to Seller Terms</h2>
      <p>
        We may revise these Seller Terms periodically. Updated terms become
        effective upon posting or notified effective date. Continued use of
        seller services constitutes acceptance.
      </p>
      <h2 className="font-semibold">17. Governing Law and Jurisdiction</h2>
      <ul>
        <li>These Seller Terms are governed by Indian law.</li>
        <li>
          Courts at Indore, Madhya Pradesh, India shall have exclusive
          jurisdiction, subject to mandatory legal rights.
        </li>
      </ul>
      <h2 className="font-semibold">18. Grievance and Contact</h2>
      <ul>
        <li>Grievance Officer: Huzefa</li>
        <li>Email: grievance@muminsmart.com</li>
        <li>Support: seller-support@muminsmart.com</li>
        <li>
          Address: A78 Hasanji Nagar, Near Emerald Heights School, Indore -
          453331
        </li>
      </ul>
    </div>
  );
};

const SellerTermsAndConditionsDialog = () => {
  return (
    <Dialog>
      <DialogTrigger>
        <button type="button" className="font-semibold text-primary underline">
          Terms and Conditions
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[85dvh] rounded-3xl">
        <DialogHeader>
          <DialogTitle>Terms and Conditions</DialogTitle>
        </DialogHeader>
        <div className="no-scrollbar max-h-[50vh] overflow-y-auto">
          <SellerTermsAndConditions />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SellerTermsAndConditionsDialog;
